import { DynamicRowHeight } from "react-window";

import { TFunction } from "i18next";

import {
    BOOLEAN_FILTER_NAMES,
    FILTER_LIST_FIELD_NAMES
} from "shared/constants/filters";
import {
    BOOLEAN_FILTER,
    INVALID,
    NOT_ASSIGNED,
    SPEC_ID_NOT_ASSIGNED_VALUE
} from "shared/constants/strings";
import {
    getFiltersWithQuotes,
    isFilter,
    isSpecIdFilter,
    isTranslatableFilter,
    safeDecode,
    transformIdArrayWithSpecIdNotAssigned
} from "shared/functions";
import {
    FILTER_NAMES,
    FILTERS_WITH_QUOTES,
    TABLE_NAMES
} from "shared/types/enums";
import { Filter, FilteredValue } from "shared/types/interfaces";
import { FilterId, QueryParamValue } from "shared/types/types";

export const filterExistingFilterNames = (
    filterNames: FILTER_NAMES[],
    existingFilterNames:
        typeof BOOLEAN_FILTER_NAMES | typeof FILTER_LIST_FIELD_NAMES,
    tableName: TABLE_NAMES
) => {
    return filterNames.filter(name =>
        existingFilterNames[
            tableName as keyof typeof existingFilterNames
        ]?.includes(name)
    );
};

export const transformedFilteredItems = (passedFilteredItems: Filter[]) =>
    passedFilteredItems.map((filteredItem: Filter) =>
        filteredItem.id === null
            ? {
                  id: "null",
                  name: NOT_ASSIGNED
              }
            : filteredItem
    );

export const updateQueryValue = (
    currentQueryValues: string[],
    id: number | string
) => {
    return currentQueryValues
        .filter(value => value !== String(id))
        .map(val => (val === SPEC_ID_NOT_ASSIGNED_VALUE ? val.split(",") : val))
        .flat();
};

export const findActualFilters = (
    filteredValues: FilteredValue[],
    filterName: FILTER_NAMES
) =>
    filteredValues.find(
        filteredValue => filteredValue.filterName === filterName
    );

export const updateFilterValues = (
    actualFilter: FilteredValue | undefined,
    id: FilterId
) =>
    actualFilter ? actualFilter.value.filter(filter => filter.id !== id) : [];

export const checkFilteredValues = (
    isInnerTable: boolean,
    innerTableFilteredValues: FilteredValue[],
    mainFilteredValues: FilteredValue[]
) => (isInnerTable ? innerTableFilteredValues : mainFilteredValues);

export const handleLoading = (isInnerTable: boolean, filtersExist: boolean) =>
    isInnerTable ? false : filtersExist;

export const getRootCompanyButtonTitle = (
    filteredCompanies: Filter[],
    rootCompanyId: string,
    rootCompany: Filter | null,
    t: any
) => {
    const companiesCount = filteredCompanies.length;

    const rootCompanyName = rootCompany?.name;

    if (companiesCount) {
        const isRootCompanyName =
            companiesCount === 1 &&
            String(filteredCompanies[0].id) === rootCompanyId;

        return isRootCompanyName
            ? rootCompanyName
            : t("Button##number of selected companies", {
                  companyCount: companiesCount,
                  companyName: rootCompanyName
              });
    }

    return t("Button##all of company", {
        companyName: rootCompanyName
    });
};

export const getCompanyFilterButtonTitle = (
    filteredCompanies: Filter[],
    noCompaniesTitle: string,
    multipleCompaniesTitle: string
) => {
    const companiesCount = filteredCompanies.length;

    switch (companiesCount) {
        case 0:
            return noCompaniesTitle;
        case 1:
            return filteredCompanies[0].name;
        default: {
            return multipleCompaniesTitle;
        }
    }
};

export const getFilterNames = (updatedParams: any) =>
    Object.keys(updatedParams).filter(
        filterName =>
            isFilter(filterName) &&
            filterName !== FILTER_NAMES.RootCompany &&
            filterName !== FILTER_NAMES.CompanyId
    ) as FILTER_NAMES[];

export const getDecodedFilterValue = (
    filterName: FILTER_NAMES,
    filterValue: QueryParamValue,
    decodeArray: (arr: string[]) => string[]
) => {
    if (Array.isArray(filterValue)) {
        const updatedFilterValue = transformIdArrayWithSpecIdNotAssigned(
            filterName,
            filterValue
        );

        return decodeArray(updatedFilterValue);
    }

    return safeDecode(filterValue || "");
};

const encodedFilterDrodownValue = (filterName: FILTER_NAMES, value: string) => {
    const filtersWithQuotes = getFiltersWithQuotes();

    const needsToBeEncoded = filtersWithQuotes.includes(
        filterName as unknown as FILTERS_WITH_QUOTES
    );

    return needsToBeEncoded ? encodeURIComponent(value) : value;
};

export const getFilterDropdownValueIds = (
    filterName: FILTER_NAMES,
    newValue: Filter[]
) => {
    const isSpecId = isSpecIdFilter(filterName);

    if (isSpecId) {
        return newValue.map(({ id }) => String(id).split(",")).flat();
    }

    if (newValue.length === 1) {
        return [encodedFilterDrodownValue(filterName, String(newValue[0].id))];
    } else {
        return newValue.map(({ id }) =>
            encodedFilterDrodownValue(filterName, String(id))
        );
    }
};

export const getBooleanFilteredValues = (
    filterNames: FILTER_NAMES[],
    tableName: TABLE_NAMES,
    currentParams: any
) => {
    const filteredValue: FilteredValue[] = [];

    const booleanFilterNames = filterExistingFilterNames(
        filterNames,
        BOOLEAN_FILTER_NAMES,
        tableName
    );

    booleanFilterNames.forEach(filterName => {
        const filterValue: string | undefined = currentParams[filterName];

        const value = [
            {
                id: filterValue || "",
                name: BOOLEAN_FILTER
            }
        ];

        filteredValue.push({
            filterName,
            value
        });
    });

    return filteredValue;
};

export const getFilterOptionLabel = (
    option: Filter,
    filter: FILTER_NAMES,
    t: TFunction<"translation", undefined>
) => {
    const name = String(option.name);
    const isTranslatable = isTranslatableFilter(filter);

    return isTranslatable || name === NOT_ASSIGNED || name === INVALID
        ? t(`General##${name}`)
        : name;
};

export const calculateVirtualizedContainerHeight = (
    totalCount: number,
    visibleCount: number,
    rowHeight: DynamicRowHeight
) => {
    let totalHeight = 0;
    const limit = Math.min(totalCount, visibleCount);

    for (let i = 0; i < limit; i++) {
        totalHeight += rowHeight.getRowHeight(i) ?? 0;
    }

    return totalHeight;
};
